import { InfoDivider, Loading, LyfeTooltip, Typography } from "@lyfegen-platform/react-common";
import { LiteBox } from "../../../../components/lite/LiteBox";
import { useTranslation } from "react-i18next";
import useOrganizationStyles from "../use-organization-styles";
import { UPDATE_DOMAIN } from "../queries";
import { useHistory, useParams } from "react-router";
import { useMutation } from "@apollo/client";
import { Button } from "@material-ui/core";
import { useDomainBreadcrumbs } from "./useDomainBreadcrumbs";
import { DomainForm } from "./components/DomainForm";
import { useOrganizationDomains } from "./useOrganizationDomains";
import { UserTable } from "./UserTable";
import { DeleteButton } from "./components/DeleteButton";
import { SwitchButton } from "./components/SwitchButton";
import { DomainTable } from "./components/DomainTable";
import { useRecoilValue } from "recoil";
import { userPersonalInfoState } from "apps/platform-client/client/src/store/user";
import SpecificErrorPage from "apps/platform-client/client/src/components/SpecificErrorPage";
import { Role } from "apps/platform-client/client/src/models/identity/role";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { findDomainById, isGlobalOrProductUser } from "./helpers";
import { DomainLevel } from "apps/platform-client/client/src/models/enums/enums";
import { firstLetterUppercase } from "apps/platform-client/client/src/utils/first-letter-uppercase";
import { DomainType, MAX_DOMAINS_LIMIT, MAX_SUBDOMAINS_LIMIT } from "./components/DomainCard";
import { useModelsMutation } from "../../../../hooks/useModelsModule";

export const UpdateDomain = () => {
	const { data, loading } = useOrganizationDomains();
	const [mutation, { error }] = useModelsMutation(UPDATE_DOMAIN);
	const { tableColumnTextColor, organizationActionSettingsContainer, pageName } = useOrganizationStyles();
	const { t } = useTranslation();
	const { push } = useHistory();
	const { domain: domainId }: { domain: string } = useParams();
	const user = useRecoilValue(userPersonalInfoState);
	const isAdmin = user.role === Role.Administrator;
	const canAccessDomains = isGlobalOrProductUser(user.domains);
	const [isOpenErrorBanner, setIsOpenErrorBanner] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const { currentDomain, parent, siblings } = data?.GetOrganizationDomainsWithCounters
		? findDomainById(data?.GetOrganizationDomainsWithCounters.domains as DomainType[], domainId)
		: { currentDomain: undefined, parent: undefined, siblings: undefined };
	const globalDomainName = firstLetterUppercase(data?.GetOrganizationDomainsWithCounters.names[0]) || "Global";
	const unavailableDomainNames: string[] =
		data?.GetOrganizationDomainsWithCounters.names.filter((dName) => dName !== currentDomain?.name.toLowerCase()) || [];

	useEffect(() => {
		error ? setIsOpenErrorBanner(true) : setIsOpenErrorBanner(false);
	}, [error]);

	const breadCrumbsProps = (level: number | undefined) => {
		if (level === DomainLevel.GLOBAL) {
			return {
				global: { id: "", name: "" },
				product: { id: "", name: "" },
				childDomainName: currentDomain?.name || "",
			};
		}
		if (level === DomainLevel.PRODUCT) {
			return {
				global: { id: "", name: globalDomainName },
				product: { id: "", name: "" },
				childDomainName: currentDomain?.name || "",
			};
		}

		return {
			global: { id: "", name: globalDomainName },
			product: { id: parent?.id || "", name: parent?.name || "" },
			childDomainName: currentDomain?.name || "",
		};
	};

	useDomainBreadcrumbs(breadCrumbsProps(currentDomain?.level));

	if (!isAdmin || !canAccessDomains) {
		return <SpecificErrorPage statusCode="403" />;
	}

	if (loading)
		return (
			<LiteBox width="100%" height="100%" justifyContent="center" alignItems="center" display="flex" padding="20vh">
				<Loading />
			</LiteBox>
		);

	const initialValues = { parentDomainId: currentDomain?.parent?.id || "", name: currentDomain?.name || "" };

	const handleSubmit = (values: { parentDomainId: string; name: string }) => {
		mutation({
			variables: { input: { id: currentDomain?.id, name: values.name } },
			onCompleted: () => {
				push("/organization-settings/domains");
			},
			onError: (error) => {
				console.error("Error updating domain:", error);
			},
		});
	};

	const handleCreateNewClick = (event: React.SyntheticEvent) => {
		event.preventDefault();

		push(`/organization-settings/domains/${currentDomain?.id}/create-domain`);
	};

	const handleSimulationClick = (event: React.SyntheticEvent, domainId: string) => {
		event.preventDefault();
		const win = window.open(`/simulator`, "_blank") as Window;
		win.sessionStorage.setItem(
			"domain",
			data?.GetOrganizationDomainsWithCounters.domains.find((domain) => domain.id === domainId)?.name || ""
		);
		win.focus();
	};

	const handleUserClick = (event: React.SyntheticEvent, domainId: string) => {
		event.preventDefault();
		const win = window.open(`/organization-settings`, "_blank") as Window;
		win.sessionStorage.setItem("domain", domainId);
		win.focus();
	};

	const addDomainOrSubdomainButton = () => {
		const isGlobal = currentDomain?.level === DomainLevel.GLOBAL;
		const domainParams = {
			limit: isGlobal ? MAX_DOMAINS_LIMIT : MAX_SUBDOMAINS_LIMIT,
			tooltipKey: isGlobal ? "product-domain-max-limit" : "product-subdomain-max-limit",
			labelKey: isGlobal ? "new-product-domain" : "new-product-subdomain",
		};
		const isLimitReached = (currentDomain?.children?.length || 0) >= domainParams.limit;

		return (
			<LyfeTooltip title={isLimitReached ? t(domainParams.tooltipKey) : ""}>
				<span>
					<Button variant="outlined" color="primary" onClick={handleCreateNewClick} disabled={isLimitReached}>
						{t(domainParams.labelKey)}
					</Button>
				</span>
			</LyfeTooltip>
		);
	};

	const localizationKeysForLevels = (level: number | undefined) => {
		if (level === DomainLevel.GLOBAL) {
			return {
				firstSection: "domain-header-general",
				subSectionTitle: "simulator-product-domains",
				label: "simulator-domain-name",
			};
		}

		if (level === DomainLevel.PRODUCT) {
			return {
				firstSection: "simulator-product-domain",
				subSectionTitle: "simulator-product-subdomains",
				label: "simulator-product-domain-name",
			};
		}

		return {
			firstSection: "simulator-product-subdomain",
			label: "simulator-product-subdomain-name",
			subSectionTitle: "",
		};
	};

	const localizationKeys = localizationKeysForLevels(currentDomain?.level);

	return (
		<LiteBox>
			<LiteBox paddingBottom={3} display="flex" justifyContent="space-between">
				<Typography className={pageName}>{`${currentDomain?.name}`}</Typography>
				{currentDomain?.level !== DomainLevel.GLOBAL && (
					<SwitchButton domainLevel={currentDomain?.level} siblingDomains={siblings || []} />
				)}
			</LiteBox>
			<LiteBox mb={5} className={organizationActionSettingsContainer}>
				<DomainForm
					initialValues={initialValues}
					domainOptions={parent ? [{ name: parent?.name, id: parent?.id }] : []}
					unavailableDomainNames={unavailableDomainNames}
					handleSubmit={handleSubmit}
					noParent={!parent}
					isError={isOpenErrorBanner}
					error={error}
					title={localizationKeys.firstSection}
					label={localizationKeys.label}
					isDeleting={isDeleting}
					dismissBanner={() => setIsOpenErrorBanner(false)}
					endAdornment={() => (
						<DeleteButton id={currentDomain?.id || ""} setIsDeleting={setIsDeleting} currentDomain={currentDomain} />
					)}
				/>
				{currentDomain?.level !== DomainLevel.SUBDOMAIN && (
					<>
						<InfoDivider
							paddingX={2}
							paddingTop={5}
							title={localizationKeys.subSectionTitle}
							endAdornment={addDomainOrSubdomainButton}
						/>
						{currentDomain?.children && currentDomain?.children.length > 0 && (
							<LiteBox paddingLeft={2}>
								<DomainTable
									domains={currentDomain?.children as DomainType[]}
									domainColumnKey={localizationKeys.subSectionTitle}
									handleUserClick={handleUserClick}
									handleSimulationClick={handleSimulationClick}
								/>
							</LiteBox>
						)}
					</>
				)}
				<InfoDivider paddingX={2} paddingTop={5} title="domain-header-users" />
				{!!currentDomain?.users.length ? (
					<UserTable users={currentDomain.users} />
				) : (
					<LiteBox minHeight={250} paddingTop={2} paddingLeft={2}>
						<Typography className={tableColumnTextColor}>
							{t("domain-view-users-empty")}{" "}
							<Link color="primary" to={"/organization-settings"}>
								{t("organization-settings-tab-users")}
							</Link>{" "}
							{t("tab")}
						</Typography>
					</LiteBox>
				)}
			</LiteBox>
		</LiteBox>
	);
};
